/**
 * Shared primitives for the Standards before/after diff table, used by both
 * `ApplyStandardsModal` and `UpdateStandardsModal` so the grid, cells, and value
 * formatting stay in sync. The two modals differ only in their column widths
 * (passed to `Grid` via `$columns`) and whether the label cell stacks its
 * contents (`LabelCell` `$stack` — Update's editable input + struck-through
 * "was …" original). Everything else is identical.
 */

import styled from 'styled-components';

import type { ControlDiffRow } from '@src/utility/compositionSettings';

/** Stable React key for a control row (property + element paths). */
export const rowKey = (control: {
  property: string;
  elementPaths: number[][];
}) => `${control.property}::${JSON.stringify(control.elementPaths)}`;

/** Coerce a value to a string, treating null/undefined as empty. */
export const asText = (value: unknown) =>
  value === undefined || value === null ? '' : String(value);

/** Human-readable, non-URL display string for a control's value. */
export const displayValue = (row: ControlDiffRow, value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    // A text with no size of its own fits itself to its box.
    return row.control.property === 'font_size' ? 'Auto' : '—';
  }
  const { type, unit } = row.control;
  if (type === 'number') return `${value}${unit ?? ''}`;
  if (type === 'image' || type === 'logo' || type === 'video') {
    const path = String(value).split('?')[0];
    return path.substring(path.lastIndexOf('/') + 1) || 'Asset';
  }
  return String(value);
};

// `$expanded` lets the Update modal's expand toggle give the table the screen's
// height instead of half of it (PLAT-11993).
export const DiffScroll = styled.div<{ $expanded?: boolean }>`
  max-height: ${({ $expanded }) =>
    $expanded ? 'calc(100vh - 240px)' : '50vh'};
  overflow-y: auto;
`;

// One element's block in the grouped Update modal: the element row heads it and
// its settings follow. A hairline divider separates consecutive blocks.
export const GroupBlock = styled.section`
  padding: var(--spacing-xs) 0;

  & + & {
    border-top: 1px solid var(--border);
  }
`;

// Heading for a block whose element has no row of its own (only settings), or
// for the Shared / placement blocks.
export const GroupTitle = styled.div`
  grid-column: 1 / -1;
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-xs);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

// `$columns` is the modal-specific grid-template-columns — the first column is
// wider in the editable Update modal than in the read-only Apply modal.
export const Grid = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  align-items: center;
  column-gap: var(--spacing-md);
`;

// Fragment children flow straight into the grid, so a row is three cells.
export const Row = styled.div`
  display: contents;
`;

export const ColumnHead = styled.div`
  padding: 0 var(--spacing-sm) var(--spacing-xs);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const cellBase = `
  padding: var(--spacing-sm);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  min-width: 0;
`;

// `$stack` lays the cell out as a column — Update stacks its editable label input
// above the struck-through "was …" original; Apply leaves the label inline.
// `$indent` sets a setting row under its element's heading; `$heading` gives the
// element row itself the heading weight (PLAT-11993).
export const LabelCell = styled.div<{
  $changed?: boolean;
  $stack?: boolean;
  $indent?: boolean;
  $heading?: boolean;
}>`
  ${cellBase}
  color: ${({ $heading }) =>
    $heading ? 'var(--foreground)' : 'var(--text-secondary)'};
  font-weight: ${({ $heading }) =>
    $heading ? 'var(--font-weight-semi-bold)' : 'inherit'};
  padding-left: ${({ $indent }) =>
    $indent
      ? 'calc(var(--spacing-sm) + var(--spacing-md))'
      : 'var(--spacing-sm)'};
  background: ${({ $changed }) =>
    $changed ? 'var(--surface-elevated)' : 'transparent'};
  ${({ $stack }) =>
    $stack
      ? `
    display: flex;
    flex-direction: column;
    gap: 2px;
  `
      : ''}
`;

// `$overflow` lets a cell hosting a picker trigger lay out without clipping.
export const ValueCell = styled.div<{
  $changed?: boolean;
  $muted?: boolean;
  $overflow?: boolean;
}>`
  ${cellBase}
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: ${({ $muted }) =>
    $muted ? 'var(--text-muted)' : 'var(--foreground)'};
  background: ${({ $changed }) =>
    $changed ? 'var(--surface-elevated)' : 'transparent'};
  overflow: ${({ $overflow }) => ($overflow ? 'visible' : 'hidden')};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Swatch = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid var(--border);
  background: ${({ $color }) => $color};
`;
